/**
 * API service for vendor-related operations
 */

import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface VendorCreationResponse {
  vendor_id: string;
  user_id: string;
  profile: any;
  personalDetails: any;
  businessDetails: any;
  kycDetails: any;
  bankAccounts: any[];
  documents: any;
}

/**
 * Creates a new vendor by submitting form data to the backend
 * @param formData - FormData object containing vendor data and files
 * @returns Promise resolving to the API response
 */
export async function createVendor(formData: FormData): Promise<VendorCreationResponse> {
  console.log('🚀 Starting vendor creation API call...');
  console.log('📡 API URL:', `${API_BASE_URL}/api/vendors`);

  try {
    console.log('📤 Sending request to backend...');
    const response = await axios.post(`${API_BASE_URL}/api/vendors`, formData);
    const responseData = response.data;
    console.log('🎉 Vendor created successfully!', responseData.data);
    return responseData.data;

  } catch (error: any) {
    console.error('💥 Error in createVendor:', error);
    throw error;
  }
}

/**
 * Validates that required files are present in FormData
 * @param formData - FormData object to validate
 * @returns true if valid, throws error if invalid
 */
export function validateFormData(formData: FormData): boolean {
  const panDocument = formData.get('panDocument');
  const aadhaarDocument = formData.get('aadhaarDocument');
  const data = formData.get('data');

  if (!data) {
    throw new Error('Missing vendor data');
  }

  if (!panDocument || !(panDocument instanceof File)) {
    throw new Error('PAN document is required');
  }

  if (!aadhaarDocument || !(aadhaarDocument instanceof File)) {
    throw new Error('Aadhaar document is required');
  }

  // Validate file types
  const allowedTypes = ['application/pdf'];

  if (!allowedTypes.includes(panDocument.type)) {
    throw new Error('PAN document must be an PDF file');
  }

  if (!allowedTypes.includes(aadhaarDocument.type)) {
    throw new Error('Aadhaar document must be an PDF file');
  }

  // Validate file sizes (max 5MB each)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes

  if (panDocument.size > maxSize) {
    throw new Error('PAN document size must be less than 5MB');
  }

  if (aadhaarDocument.size > maxSize) {
    throw new Error('Aadhaar document size must be less than 5MB');
  }

  return true;
}

